import React, { useState } from 'react';
import { Users, BookOpen, TrendingUp, AlertCircle } from 'lucide-react';
import { useDashboard } from '@/hooks/reporting/useDashboard';
import MetricsCard from '@/components/reports/cards/MetricsCard';
import LineChart from '@/components/reports/charts/LineChart';
import PieChart from '@/components/reports/charts/PieChart';
import ProgressBar from '@/components/reports/charts/ProgressBar';
import EmployeePerformanceTable from '@/components/reports/tables/EmployeePerformanceTable';
import PeriodTypeSelector from '@/components/reports/filters/PeriodTypeSelector';
import DateRangePicker from '@/components/reports/filters/DateRangePicker';
import CacheIndicator from '@/components/reports/common/CacheIndicator';
import EmptyState from '@/components/reports/common/EmptyState';
import type { PeriodType, ManagerTeamRequest } from '@/types/reporting-v2';

// Helper function to get normalized date range for cache optimization
const getNormalizedDateRange = (periodType: PeriodType) => {
  const end_date = new Date();
  end_date.setHours(23, 59, 59, 999);

  const start_date = new Date();
  start_date.setHours(0, 0, 0, 0);

  switch (periodType) {
    case 'daily':
      break;
    case 'weekly':
      start_date.setDate(start_date.getDate() - 7);
      break;
    case 'monthly':
      start_date.setMonth(start_date.getMonth() - 1);
      break;
    case 'quarterly':
      start_date.setMonth(start_date.getMonth() - 3);
      break;
    case 'yearly':
      start_date.setFullYear(start_date.getFullYear() - 1);
      break;
    case 'custom':
      break;
  }

  return {
    start_date: start_date.toISOString(),
    end_date: end_date.toISOString()
  };
};

export default function ManagerTeam() {
  const initialDates = React.useMemo(() => getNormalizedDateRange('monthly'), []);

  const [params, setParams] = useState<ManagerTeamRequest>({
    period_type: 'monthly',
    start_date: initialDates.start_date,
    end_date: initialDates.end_date,
  });

  const { data, isLoading, error, refetch } = useDashboard('manager-team', params);

  const handlePeriodChange = (period: PeriodType) => {
    if (period !== 'custom') {
      const normalizedDates = getNormalizedDateRange(period);
      setParams({
        period_type: period,
        start_date: normalizedDates.start_date,
        end_date: normalizedDates.end_date
      });
    } else {
      setParams(prev => ({ ...prev, period_type: period }));
    }
  };

  const handleDateRangeChange = (start: string, end: string) => {
    const start_date = new Date(start);
    start_date.setHours(0, 0, 0, 0);

    const end_date = new Date(end);
    end_date.setHours(23, 59, 59, 999);

    setParams({
      period_type: 'custom',
      start_date: start_date.toISOString(),
      end_date: end_date.toISOString()
    });
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error loading dashboard</h3>
          <p className="text-red-600 text-sm mt-1">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manager Team Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track your team's learning progress and performance
          </p>
        </div>
        {data && (
          <CacheIndicator
            metadata={{
              cached_at: data.generated_at,
              is_fresh: !data.cached,
              ttl_seconds: data.cache_expires_at ? Math.floor((new Date(data.cache_expires_at).getTime() - new Date().getTime()) / 1000) : undefined
            }}
            onRefresh={() => refetch()}
            refreshing={isLoading}
          />
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Period
            </label>
            <PeriodTypeSelector value={params.period_type} onChange={handlePeriodChange} />
          </div>

          {params.period_type === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Custom Date Range</label>
              <DateRangePicker
                startDate={params.start_date}
                endDate={params.end_date}
                onChange={handleDateRangeChange}
              />
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
                  <div className="h-8 bg-gray-200 rounded w-32 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-20" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : !data ? (
        <EmptyState
          icon={Users}
          title="No data available"
          description="Unable to load team dashboard. Please try again later."
        />
      ) : (
        <>
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.widgets?.metric_cards?.map((card: any, index: number) => {
              const iconMap: Record<string, any> = {
                'users': Users,
                'user-check': TrendingUp,
                'check-circle': BookOpen,
                'star': TrendingUp,
                'clock': BookOpen,
                'award': TrendingUp,
              };
              const Icon = iconMap[card.icon] || Users;
              const colorMap: Record<number, string> = {
                0: 'primary',
                1: 'success',
                2: 'info',
                3: 'warning',
                4: 'info',
                5: 'success',
              };

              return (
                <MetricsCard
                  key={index}
                  title={card.title}
                  value={typeof card.value === 'string' ? card.value : card.value?.toLocaleString()}
                  change={card.change_percentage}
                  icon={Icon}
                  color={colorMap[index] || 'primary'}
                  loading={false}
                />
              );
            })}
          </div>

          {/* Completion Trend */}
          {data.widgets?.completion_trend?.labels?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Team Completion Trend</h2>
              <LineChart
                labels={data.widgets.completion_trend.labels}
                series={[
                  {
                    name: data.widgets.completion_trend.datasets?.[0]?.label || 'Completions',
                    data: data.widgets.completion_trend.datasets?.[0]?.data || [],
                    color: data.widgets.completion_trend.datasets?.[0]?.borderColor || '#10b981'
                  }
                ]}
                height={300}
              />
            </div>
          )}

          {/* Engagement Chart */}
          {data.widgets?.engagement_chart?.labels?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Team Activity</h2>
              <PieChart
                data={data.widgets.engagement_chart.labels.map((label: string, index: number) => ({
                  label,
                  value: data.widgets.engagement_chart.datasets?.[0]?.data?.[index] || 0,
                  color: data.widgets.engagement_chart.datasets?.[0]?.backgroundColor?.[index] || '#3b82f6',
                }))}
                height={300}
              />
            </div>
          )}

          {/* At Risk Learners */}
          {data.widgets?.at_risk_learners && data.widgets.at_risk_learners.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-red-900 mb-4">
                At Risk Team Members
              </h2>
              <div className="space-y-3">
                {data.widgets.at_risk_learners.map((member: any, index: number) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg border border-red-200"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{member.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{member.reason}</p>
                      </div>
                      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                        At Risk
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Completions */}
          {data.widgets?.recent_completions && data.widgets.recent_completions.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Team Completions</h2>
              <div className="space-y-3">
                {data.widgets.recent_completions.map((completion: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-green-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{completion.course_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">{completion.user_name}</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">
                          {new Date(completion.completed_at).toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">
                          {completion.time_spent_hours.toFixed(2)}h
                        </span>
                      </div>
                    </div>
                    <span className="flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      Completed
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
